import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useCreatePlmCurtailmentActionMutation,
  useUpdatePlmCurtailmentActionMutation,
  EnumPlmCurtailmentActionDeviceType,
  PlmCurtailmentAction,
  useQueryableFieldsQuery,
  TieredEntitySelectionInput,
} from '../../../../types/generated-types';
import { Notifier } from '../../../system/services/notificationManager';
import type { SelectChangeEvent } from '@mui/material/Select';
import BorderedSection from '../../shared/borderedSection';
import { PlmCurtailmentPlanDetailDocument } from '../../../../types/generated-types';
import {
  EntitySelectionBuilder,
  EntitySelectionCriteriaBuilder,
} from '../../../common/entitySelection';
import { ITieredEntitySelection } from '../../../../common/types/shared/entitySelection';
import { EntityOperator } from '../../../../common/types/shared/entitySelection';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import {
  defaultEntityOperator,
  defaultTieredEntitySelection,
} from '../../../common/entitySelection/helpers/constructors';
import { TieredEntitySelectionBuilder } from '../../../common/entitySelection/components/TieredEntitySelectionBuilder';

interface CurtailmentActionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  planId?: string;
  action?: PlmCurtailmentAction | null;
  onSuccess: () => void;
}

const defaultDeviceType = EnumPlmCurtailmentActionDeviceType.Thermostat;
const ABSOLUTE_SETPOINT_ADJUSTMENT = 'ABSOLUTE';
const RELATIVE_SETPOINT_ADJUSTMENT = 'RELATIVE_OFFSET';

// Map of device type to valid adjustment attributes and their types
const DEVICE_TYPE_ATTRIBUTES: Record<
  EnumPlmCurtailmentActionDeviceType,
  {
    name: string;
    type: 'number' | 'string' | 'boolean';
    supportsRelativeOffset?: boolean;
  }[]
> = {
  [EnumPlmCurtailmentActionDeviceType.Thermostat]: [
    { name: 'maxCoolSetpointLimit', type: 'number' },
    { name: 'maxHeatSetpointLimit', type: 'number' },
    { name: 'minCoolSetpointLimit', type: 'number' },
    { name: 'minHeatSetpointLimit', type: 'number' },
    { name: 'coolSetpoint', type: 'number', supportsRelativeOffset: true },
    { name: 'heatSetpoint', type: 'number', supportsRelativeOffset: true },
    { name: 'fanMode', type: 'string' },
    { name: 'fanSpeed', type: 'string' },
    { name: 'keypadLockoutLevel', type: 'string' },
    { name: 'operatingMode', type: 'string' },
  ],
  [EnumPlmCurtailmentActionDeviceType.SmartPowerOutlet]: [
    { name: 'isOn', type: 'boolean' },
  ],
  [EnumPlmCurtailmentActionDeviceType.ModbusAirHandler]: [
    { name: 'freshAirSetpoint', type: 'number' },
    { name: 'rhSetpoint', type: 'number' },
    { name: 'ventilationMode', type: 'string' },
  ],
};

interface FormValues {
  name: string;
  description: string;
  deviceType: EnumPlmCurtailmentActionDeviceType | '';
  estimatedReductionKW: number;
  targetDevices: TieredEntitySelectionInput;
  adjustments: AdjustmentState;
  startTimeOffset: number;
  autoBatch: boolean;
  batchCount: number;
  batchDuration: number;
  batchStartTimeOffset: number;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  deviceType: Yup.string().required('Device type is required'),
  estimatedReductionKW: Yup.number()
    .required('Estimated reduction is required')
    .min(0, 'Must be positive'),
  targetDevices: Yup.object().required(
    'Target devices configuration is required',
  ),
  adjustments: Yup.object(),
  startTimeOffset: Yup.number().required('Start time offset is required'),
  autoBatch: Yup.boolean().required('Auto batch setting is required'),
  batchCount: Yup.number()
    .when('autoBatch', {
      is: true,
      then: (schema) =>
        schema
          .required('Batch count is required when auto batching is enabled')
          .min(1, 'Must be at least 1'),
    })
    .when('autoBatch', {
      is: false,
      then: (schema) => schema.optional(),
    }),
  batchDuration: Yup.number()
    .when('autoBatch', {
      is: true,
      then: (schema) =>
        schema
          .required('Batch duration is required when auto batching is enabled')
          .min(1, 'Must be at least 1'),
    })
    .when('autoBatch', {
      is: false,
      then: (schema) => schema.optional(),
    }),
  batchStartTimeOffset: Yup.number()
    .when('autoBatch', {
      is: true,
      then: (schema) =>
        schema.required(
          'Batch start time offset is required when auto batching is enabled',
        ),
    })
    .when('autoBatch', {
      is: false,
      then: (schema) => schema.optional(),
    }),
});

interface AdjustmentState {
  [attribute: string]: {
    enabled: boolean;
    value: any;
    adjustmentMode?: string;
  };
}

const getInitialAdjustmentState = (
  deviceType: EnumPlmCurtailmentActionDeviceType | '',
  raw_adjustments?: any,
): AdjustmentState => {
  const adjustments = raw_adjustments?.length ? raw_adjustments[0] : {};
  const attrs =
    DEVICE_TYPE_ATTRIBUTES[deviceType as EnumPlmCurtailmentActionDeviceType] ||
    [];
  const state: AdjustmentState = {};

  attrs.forEach((attr) => {
    const rawValue = adjustments ? adjustments[attr.name] : undefined;
    const rawAdjustmentMode = adjustments
      ? adjustments[`${attr.name}AdjustmentMode`]
      : undefined;
    state[attr.name] = {
      enabled: rawValue !== undefined && rawValue !== null,
      value:
        rawValue !== undefined && rawValue !== null
          ? rawValue
          : attr.type === 'boolean'
            ? false
            : '',
      adjustmentMode:
        attr.supportsRelativeOffset && rawAdjustmentMode
          ? rawAdjustmentMode
          : ABSOLUTE_SETPOINT_ADJUSTMENT,
    };
  });

  return state;
};

export const CurtailmentActionDetailDialog: React.FC<
  CurtailmentActionDetailDialogProps
> = ({ open, onClose, action, planId, onSuccess }) => {
  const [createAction] = useCreatePlmCurtailmentActionMutation();
  const [updateAction] = useUpdatePlmCurtailmentActionMutation();
  const [tab, setTab] = React.useState(0);

  // Memoize the initial form values
  const initialFormValues = React.useMemo(() => {
    return {
      name: action?.name || '',
      description: action?.description || '',
      deviceType: action?.deviceType || defaultDeviceType,
      estimatedReductionKW: action?.estimatedReductionKW ?? 0,
      targetDevices:
        action?.targetDevices ??
        defaultTieredEntitySelection(action?.deviceType ?? defaultDeviceType),
      adjustments: getInitialAdjustmentState(
        action?.deviceType || defaultDeviceType,
        action?.targetedAdjustments,
      ),
      startTimeOffset: action?.startTimeOffset ?? 0,
      autoBatch: action?.autoBatch ?? false,
      batchCount: action?.batchCount ?? 3,
      batchDuration: action?.batchDuration ?? 15,
      batchStartTimeOffset: action?.batchStartTimeOffset ?? 0,
    };
  }, [action]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!(action?._id && action?.plmCurtailmentPlanId) && !planId) return;

      // Build targetedAdjustments object
      const attrs =
        DEVICE_TYPE_ATTRIBUTES[
          values.deviceType as EnumPlmCurtailmentActionDeviceType
        ] || [];
      const adj: any = { type: values.deviceType };

      attrs.forEach((attr) => {
        if (values.adjustments[attr.name]?.enabled) {
          adj[attr.name] = values.adjustments[attr.name].value;
          if (attr.supportsRelativeOffset) {
            adj[`${attr.name}AdjustmentMode`] =
              values.adjustments[attr.name].adjustmentMode ??
              ABSOLUTE_SETPOINT_ADJUSTMENT;
          }
        }
      });

      const mutationVariables = {
        _id: action?._id ?? '',
        action: {
          name: values.name,
          description: values.description,
          deviceType: values.deviceType,
          estimatedReductionKW: values.estimatedReductionKW,
          targetDevices: values.targetDevices,
          plmCurtailmentPlanId: action?.plmCurtailmentPlanId ?? planId ?? '',
          targetedAdjustments: adj,
          startTimeOffset: values.startTimeOffset,
          autoBatch: values.autoBatch,
          batchCount: values.batchCount,
          batchDuration: values.batchDuration,
          batchStartTimeOffset: values.batchStartTimeOffset,
        },
      };

      try {
        if (action?._id) {
          await updateAction({
            variables: mutationVariables,
            refetchQueries: [
              {
                query: PlmCurtailmentPlanDetailDocument,
                variables: { _id: action?.plmCurtailmentPlanId ?? planId },
              },
            ],
          });
        } else {
          const newMutationVariables = {
            action: mutationVariables.action,
          };

          await createAction({
            variables: newMutationVariables,
            refetchQueries: [
              {
                query: PlmCurtailmentPlanDetailDocument,
                variables: { _id: action?.plmCurtailmentPlanId ?? planId },
              },
            ],
          });
        }

        Notifier.success(
          `Curtailment action ${action?._id ? 'updated' : 'created'} successfully`,
        );

        resetForm();
        onSuccess();
        onClose();
      } catch (error) {
        Notifier.error('Failed to update curtailment action');
      }
    },
  });

  const adjustmentAttributes =
    DEVICE_TYPE_ATTRIBUTES[
      formik.values.deviceType as EnumPlmCurtailmentActionDeviceType
    ] || [];

  const handleDeviceTypeChange = React.useCallback(
    (event: SelectChangeEvent<EnumPlmCurtailmentActionDeviceType>) => {
      formik.handleChange(event);
      const newDeviceType = event.target
        .value as EnumPlmCurtailmentActionDeviceType;
      formik.setFieldValue(
        'adjustments',
        getInitialAdjustmentState(newDeviceType),
      );
      formik.setFieldValue(
        'targetDevices',
        defaultTieredEntitySelection(newDeviceType),
      );
    },
    [formik],
  );

  const isSmall = useIsSmallScreen();

  // Memoize the query variables to prevent unnecessary re-renders
  const queryVariables = React.useMemo(
    () => ({
      modelName: {
        single: formik.values.deviceType ?? '',
      },
    }),
    [formik.values.deviceType],
  );

  const {
    data: fieldsData,
    loading,
    error: fieldsErrors,
  } = useQueryableFieldsQuery({
    variables: queryVariables,
    skip: !formik.values.deviceType,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          my: 1,
          height: 'calc(100% - 16px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        {action?._id ? 'Edit' : 'Add'} Curtailment Action
      </DialogTitle>
      <form
        onSubmit={formik.handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            p: 0,
            pt: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 0.5, pb: 0 }}>
            {/* Top section: name, description, estimated reduction, device type */}
            <TextField
              fullWidth
              margin="dense"
              size="small"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              margin="dense"
              size="small"
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
            <TextField
              fullWidth
              margin="dense"
              size="small"
              name="estimatedReductionKW"
              label="Estimated Reduction (kW)"
              type="number"
              value={formik.values.estimatedReductionKW}
              onChange={formik.handleChange}
              error={
                formik.touched.estimatedReductionKW &&
                Boolean(formik.errors.estimatedReductionKW)
              }
              helperText={
                formik.touched.estimatedReductionKW &&
                formik.errors.estimatedReductionKW
              }
            />
          </Box>

          {/* Device Type and Adjustments Section */}
          <BorderedSection
            title="Targeted Devices and Adjustments"
            style={{
              margin: '4px',
              marginTop: '8px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              marginBottom: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Device Type</InputLabel>
                <Select
                  name="deviceType"
                  value={formik.values.deviceType}
                  onChange={handleDeviceTypeChange}
                  label="Device Type"
                  error={
                    formik.touched.deviceType &&
                    Boolean(formik.errors.deviceType)
                  }
                >
                  {Object.values(EnumPlmCurtailmentActionDeviceType).map(
                    (type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>

              {/* Two-tab panel for Adjustments and Targeted Devices */}
              {fieldsErrors ? (
                <div>
                  Error loading Field Info{' '}
                  {JSON.stringify(fieldsErrors, null, 2)}
                </div>
              ) : loading ? (
                <div>Loading fields info ...</div>
              ) : fieldsData ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    mt: 0.5,
                  }}
                >
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                      <Tab label={'Scheduling'} />
                      <Tab label={'Adjustments'} />
                      <Tab label={isSmall ? 'Devices' : 'Targeted Devices'} />
                    </Tabs>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      p: 1.5,
                      maxHeight: '400px', // Add a maximum height to ensure scrolling works
                    }}
                  >
                    {tab === 0 && (
                      <Box>
                        {/* Scheduling Fields */}
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          name="startTimeOffset"
                          label="Start Time Offset (minutes)"
                          type="number"
                          value={formik.values.startTimeOffset}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.startTimeOffset &&
                            Boolean(formik.errors.startTimeOffset)
                          }
                          helperText={
                            formik.touched.startTimeOffset &&
                            formik.errors.startTimeOffset
                              ? formik.errors.startTimeOffset
                              : 'Number of minutes before or after event start time'
                          }
                        />

                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                        >
                          <input
                            type="checkbox"
                            checked={formik.values.autoBatch}
                            onChange={(e) => {
                              formik.setFieldValue(
                                'autoBatch',
                                e.target.checked,
                              );
                              if (!e.target.checked) {
                                // Reset batch-related fields to defaults when autoBatch is unchecked
                                formik.setFieldValue('batchCount', 3);
                                formik.setFieldValue('batchDuration', 15);
                                formik.setFieldValue('batchStartTimeOffset', 0);
                              }
                            }}
                            style={{ marginRight: 8 }}
                          />
                          <span style={{ fontSize: '0.875rem' }}>
                            Auto Batch Devices
                          </span>
                        </Box>

                        {formik.values.autoBatch && (
                          <>
                            <TextField
                              fullWidth
                              margin="dense"
                              size="small"
                              name="batchCount"
                              label="Number of Batches"
                              type="number"
                              value={formik.values.batchCount}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.batchCount &&
                                Boolean(formik.errors.batchCount)
                              }
                              helperText={
                                formik.touched.batchCount &&
                                formik.errors.batchCount
                                  ? formik.errors.batchCount
                                  : 'Number of batches to divide devices into'
                              }
                            />

                            <TextField
                              fullWidth
                              margin="dense"
                              size="small"
                              name="batchDuration"
                              label="Batch Duration (minutes)"
                              type="number"
                              value={formik.values.batchDuration}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.batchDuration &&
                                Boolean(formik.errors.batchDuration)
                              }
                              helperText={
                                formik.touched.batchDuration &&
                                formik.errors.batchDuration
                                  ? formik.errors.batchDuration
                                  : 'Maximum time per batch per cycle'
                              }
                            />

                            <TextField
                              fullWidth
                              margin="dense"
                              size="small"
                              name="batchStartTimeOffset"
                              label="Batch Start Time Offset (minutes)"
                              type="number"
                              value={formik.values.batchStartTimeOffset}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.batchStartTimeOffset &&
                                Boolean(formik.errors.batchStartTimeOffset)
                              }
                              helperText={
                                formik.touched.batchStartTimeOffset &&
                                formik.errors.batchStartTimeOffset
                                  ? formik.errors.batchStartTimeOffset
                                  : 'Offset from batch scheduled start time'
                              }
                            />
                          </>
                        )}
                      </Box>
                    )}
                    {tab === 1 && (
                      <Box>
                        {adjustmentAttributes.length > 0 && (
                          <>
                            {adjustmentAttributes.map((attr) => (
                              <Box
                                key={attr.name}
                                data-adjustment-row={attr.name}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <FormControlLabel
                                  sx={{ minWidth: 180, m: 0 }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={
                                        formik.values.adjustments[attr.name]
                                          ?.enabled || false
                                      }
                                      onChange={(e) => {
                                        if (
                                          e.target.checked !==
                                          formik.values.adjustments[attr.name]
                                            .enabled
                                        ) {
                                          const newValue = {
                                            value: !e.target.checked
                                              ? attr.type === 'boolean'
                                                ? false
                                                : ''
                                              : formik.values.adjustments[
                                                  attr.name
                                                ].value,
                                            enabled: e.target.checked,
                                            adjustmentMode:
                                              formik.values.adjustments[
                                                attr.name
                                              ].adjustmentMode ??
                                              ABSOLUTE_SETPOINT_ADJUSTMENT,
                                          };

                                          formik.setFieldValue('adjustments', {
                                            ...formik.values.adjustments,
                                            [attr.name]: newValue,
                                          });
                                        }
                                      }}
                                    />
                                  }
                                  label={attr.name}
                                  componentsProps={{
                                    typography: {
                                      sx: { fontSize: '0.875rem' },
                                    },
                                  }}
                                />
                                {attr.supportsRelativeOffset && (
                                  <FormControl
                                    size="small"
                                    margin="dense"
                                    sx={{ width: 190 }}
                                    disabled={
                                      !formik.values.adjustments[attr.name]
                                        ?.enabled
                                    }
                                  >
                                    <InputLabel
                                      id={`${attr.name}-adjustment-mode-label`}
                                    >
                                      {attr.name} adjustment mode
                                    </InputLabel>
                                    <Select
                                      labelId={`${attr.name}-adjustment-mode-label`}
                                      label={`${attr.name} adjustment mode`}
                                      value={
                                        formik.values.adjustments[attr.name]
                                          ?.adjustmentMode ??
                                        ABSOLUTE_SETPOINT_ADJUSTMENT
                                      }
                                      onChange={(e) => {
                                        formik.setFieldValue('adjustments', {
                                          ...formik.values.adjustments,
                                          [attr.name]: {
                                            ...formik.values.adjustments[
                                              attr.name
                                            ],
                                            adjustmentMode: e.target.value,
                                          },
                                        });
                                      }}
                                    >
                                      <MenuItem
                                        value={ABSOLUTE_SETPOINT_ADJUSTMENT}
                                      >
                                        Absolute target
                                      </MenuItem>
                                      <MenuItem
                                        value={RELATIVE_SETPOINT_ADJUSTMENT}
                                      >
                                        Relative offset
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                                <TextField
                                  sx={{ flex: 1 }}
                                  size="small"
                                  margin="dense"
                                  label={attr.name}
                                  type={
                                    attr.type === 'number' ? 'number' : 'text'
                                  }
                                  value={
                                    formik.values.adjustments[attr.name]
                                      ?.value ?? ''
                                  }
                                  onChange={(e) => {
                                    // Convert the value to the appropriate type
                                    let convertedValue: any = e.target.value;

                                    if (attr.type === 'number') {
                                      // Convert to number, but handle empty string
                                      convertedValue =
                                        e.target.value === ''
                                          ? ''
                                          : Number(e.target.value);
                                    } else if (attr.type === 'boolean') {
                                      // Convert to boolean
                                      convertedValue =
                                        e.target.value === 'true' ||
                                        e.target.value === '1';
                                    }
                                    // For string type, keep as is

                                    formik.setFieldValue('adjustments', {
                                      ...formik.values.adjustments,
                                      [attr.name]: {
                                        ...formik.values.adjustments[attr.name],
                                        value: convertedValue,
                                      },
                                    });
                                  }}
                                  disabled={
                                    !formik.values.adjustments[attr.name]
                                      ?.enabled
                                  }
                                />
                              </Box>
                            ))}
                          </>
                        )}
                        {/*<EntitySelectionCriteriaBuilder*/}
                        {/*  criteria={{ criteria: [] }}*/}
                        {/*  onChange={(val) => {*/}
                        {/*    console.log('values', JSON.stringify(val, null, 2));*/}
                        {/*  }}*/}
                        {/*  targetModel={formik.values.deviceType}*/}
                        {/*/>*/}
                      </Box>
                    )}
                    {tab === 2 && (
                      <Box>
                        <TieredEntitySelectionBuilder
                          value={formik.values.targetDevices}
                          onChangedValue={(
                            updatedValue: TieredEntitySelectionInput,
                          ) => {
                            formik.setFieldValue('targetDevices', updatedValue);
                          }}
                          onClose={() => {
                            // alert('Dialog closed!');
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                <div>
                  {formik.values.deviceType
                    ? `No fields data found for selected device type: ${formik.values.deviceType}`
                    : 'Select device type to continue ...'}
                </div>
              )}
            </Box>
          </BorderedSection>
        </DialogContent>
        <DialogActions sx={{ p: 1, pt: 0.5, mt: 'auto' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {action?._id ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
